import { Dialog, Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import {
  FC,
  Fragment,
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MODAL_TYPES, POSITION, SIZE } from '../../../utils/constants';
import { cn } from '../../../utils/helpers';

const modalClasses = cva(['fixed', 'bg-white shadow', 'rounded-lg'], {
  variants: {
    size: {
      [SIZE.Small]: ['w-[320px]'],
      [SIZE.Medium]: ['w-[440px]'],
      [SIZE.Large]: ['w-[600px]'],
    },
    position: {
      [POSITION.Center]: [],
      [POSITION.Top]: ['top-0'],
      [POSITION.TopRight]: ['top-0 right-0'],
      [POSITION.TopLeft]: ['top-0 left-0'],
      [POSITION.Bottom]: ['bottom-0'],
      [POSITION.BottomRight]: ['bottom-0 right-0'],
      [POSITION.BottomLeft]: ['bottom-0 left-0'],
      [POSITION.Left]: ['left-0'],
      [POSITION.Right]: ['right-0'],
    },
  },
  defaultVariants: { size: SIZE.Medium, position: POSITION.Center },
});

const modalHeaderClasses = cva(['flex items-center justify-between', 'bg-light-gray px-4 py-2.5', 'rounded-t-lg'], {
  variants: {
    type: {
      [MODAL_TYPES.SUCCESS]: ['bg-positve'],
      [MODAL_TYPES.INFO]: ['bg-warning'],
      [MODAL_TYPES.WARNING]: ['bg-negative text-white'],
    },
  },
  defaultVariants: {
    type: MODAL_TYPES.SUCCESS,
  },
});

interface ShowModalArgs {
  content: ReactNode;
  title: string;
  disableOutsideClick?: boolean;
  hideHeader?: boolean;
  size?: SIZE;
  position?: POSITION;
  type?: MODAL_TYPES;
}

interface ModalInterface {
  showModal: ({ content, title, disableOutsideClick, hideHeader, size, position }: ShowModalArgs) => void;
  hideModal: () => void;
}

interface ModalWrapperProps {
  content?: ReactNode;
  title?: string;
  isOpen: boolean;
  closeModal: () => void;
  disableOutsideClick?: boolean;
  hideHeader?: boolean;
  size?: SIZE;
  position?: POSITION;
  type?: MODAL_TYPES;
}

export const ModalContext = createContext<ModalInterface>({
  hideModal: () => {},
  showModal: () => {},
});

export const ModalWrapper: FC<ModalWrapperProps> = ({
  content,
  title,
  isOpen,
  closeModal,
  disableOutsideClick,
  hideHeader,
  size,
  position,
  type,
}) => {
  const handleCloseModal = () => {
    if (disableOutsideClick) {
      return;
    }

    closeModal();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog className="relative z-50" onClose={handleCloseModal} static>
        <div className="fixed inset-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className={cn(
                'min-h-full',
                'overflow-y-auto',
                'flex items-center justify-center',
                'bg-[#00000090] backdrop-blur-sm',
              )}
            >
              <Dialog.Panel className={cn(modalClasses({ size, position }))}>
                {!hideHeader && (
                  <div className={cn(modalHeaderClasses({ type }))}>
                    <Dialog.Title as="h3">{title}</Dialog.Title>
                    <button onClick={closeModal} type="button" className="ml-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M0.44728 0.44728C0.733759 0.160887 1.12226 0 1.52734 0C1.93242 0 2.32092 0.160887 2.6074 0.44728L8.00922 5.8491L13.411 0.44728C13.6992 0.169003 14.0851 0.015023 14.4856 0.0185037C14.8862 0.0219843 15.2693 0.182647 15.5526 0.465889C15.8358 0.74913 15.9965 1.13229 15.9999 1.53284C16.0034 1.93339 15.8494 2.31928 15.5712 2.6074L10.1693 8.00922L15.5712 13.411C15.8494 13.6992 16.0034 14.0851 15.9999 14.4856C15.9965 14.8862 15.8358 15.2693 15.5526 15.5526C15.2693 15.8358 14.8862 15.9965 14.4856 15.9999C14.0851 16.0034 13.6992 15.8494 13.411 15.5712L8.00922 10.1693L2.6074 15.5712C2.31928 15.8494 1.93339 16.0034 1.53284 15.9999C1.13229 15.9965 0.74913 15.8358 0.465889 15.5526C0.182647 15.2693 0.0219843 14.8862 0.0185037 14.4856C0.015023 14.0851 0.169003 13.6992 0.44728 13.411L5.8491 8.00922L0.44728 2.6074C0.160887 2.32092 0 1.93242 0 1.52734C0 1.12226 0.160887 0.733759 0.44728 0.44728Z"
                          fill={type === MODAL_TYPES.WARNING ? '#FFFFFF' : '#2F2F2F'}
                        />
                      </svg>
                    </button>
                  </div>
                )}
                <div className="flex gap-4 flex-col rounded-b-lg">{content}</div>
              </Dialog.Panel>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

const Modal: FC<PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSize, setModalSize] = useState<SIZE>(SIZE.Large);
  const [modalPosition, setModalPosition] = useState<POSITION>(POSITION.Center);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [hideModalHeader, setHideModalHeader] = useState(false);
  const [modalDisableOutsideClick, setDisableOutsideClick] = useState(false);
  const [modalType, setModalType] = useState(MODAL_TYPES.SUCCESS);

  const reset = () => {
    setHideModalHeader(false);
    setDisableOutsideClick(false);
    setModalTitle('');
    setModalSize(SIZE.Large);
    setModalPosition(POSITION.Center);
    setModalContent(null);
  };

  const showModal = useCallback(
    ({
      content,
      title,
      disableOutsideClick = false,
      hideHeader = false,
      size = SIZE.Large,
      position = POSITION.Center,
      type = MODAL_TYPES.SUCCESS,
    }: ShowModalArgs) => {
      setModalPosition(position);
      setModalTitle(title);
      setModalSize(size);
      setDisableOutsideClick(disableOutsideClick);
      setHideModalHeader(hideHeader);
      setModalContent(content);
      setModalType(type);
    },
    [],
  );

  const hideModal = useCallback(() => {
    setOpen(false);

    // reset after transition duration
    setTimeout(reset, 201);
  }, []);

  useEffect(() => {
    setOpen(!!modalContent);
  }, [modalContent]);

  const value = useMemo(() => ({ showModal, hideModal }), [showModal, hideModal]);

  return (
    <ModalContext.Provider value={value}>
      <ModalWrapper
        disableOutsideClick={modalDisableOutsideClick}
        isOpen={open}
        closeModal={hideModal}
        content={modalContent}
        hideHeader={hideModalHeader}
        title={modalTitle}
        size={modalSize}
        position={modalPosition}
        type={modalType}
      />
      {children}
    </ModalContext.Provider>
  );
};

export default Modal;
